import React, { useEffect, useRef, useState } from 'react';
import SelectField from '../components/SelectField';
import MultiSelect from '../components/MultiSelect';
import LoadingState from '../components/LoadingState';
import { useNavigate, useParams } from 'react-router-dom';
import { listTemplates, fetchCampaign, createCampaignApi, updateCampaignTemplatesApi, createTemplateApi, listCampaignsApi, presign, addKbFileToCampaign, deleteKbFileFromCampaign } from '../api/api';
import ConfirmationModal from '../components/ConfirmationModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import useUnsavedChangesGuard from '../hooks/useUnsavedChangesGuard';
import {
  FaFileAlt,
  FaPlus,
  FaSave,
  FaTimes,
  FaTrash
} from 'react-icons/fa';
import FileUpload from '../components/FileUpload';
import axios from 'axios';
import { ASSESSMENT_TYPE_OPTIONS, isWeightedType } from '../common/assessment';
import { withSortOrder } from '../common/questionOrder';

const CampaignEditor = ({ setAlert }) => {
  const navigate = useNavigate();
  const { name } = useParams();
  const [campaignName, setCampaignName] = useState('');
  const [templates, setTemplates] = useState([]); // all templates
  const [selected, setSelected] = useState([]); // selected template names
  const [initial, setInitial] = useState([]);
  const [newTemplates, setNewTemplates] = useState([]); // templates created inline
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveConfirm, setSaveConfirm] = useState(false);
  const [allCamps, setAllCamps] = useState('')
  const [inputError, setInputError] = useState([false]);
  // A failed save must land the user on the error, not leave them wherever
  // the confirm dialog was — the banner sits near the save row and can be
  // far off-screen on long forms.
  const errorRef = useRef(null);
  useEffect(() => {
    if (inputError[0]) {
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [inputError]);
  const [upload, setUpload] = useState([])
  const [kbFiles, setKbFiles] = useState([]);
  const [deleteKbInfo, setDeleteKbInfo] = useState({ open: false, fileId: null });
  // Set after a successful save so the unsaved-changes guard does not fire on
  // the post-save navigation back to the campaign list.
  const [saved, setSaved] = useState(false);
  // No separate dirty flag to keep in sync — derived straight from the
  // state that already distinguishes "loaded" from "current". In create
  // mode (no `name`) there's no loaded snapshot to diff against, so any
  // input at all counts as unsaved work.
  const dirty = !saved && (name
    ? JSON.stringify(selected) !== JSON.stringify(initial) || newTemplates.length > 0 || upload.length > 0
    : campaignName.trim() !== '' || selected.length > 0 || newTemplates.length > 0 || upload.length > 0);
  const blocker = useUnsavedChangesGuard(dirty);

  // Navigate on the render after `saved` flips, so the blocker has already
  // re-registered with dirty=false; navigating synchronously inside
  // handleSave would still hit the stale (dirty=true) blocker.
  useEffect(() => {
    if (saved) navigate('/campaigns');
  }, [saved, navigate]);

    const handleFileAdd = ({ detail }) => {
    const newFiles = detail.value;
    setUpload(newFiles);
  };

  useEffect(() => {
    const load = async () => {
      try {
        const tmplRes = await listTemplates();
        setTemplates(tmplRes.templates || []);
        const res = await listCampaignsApi();
        setAllCamps(res.campaigns)
        if (name) {
          const campRes = await fetchCampaign(name);
          setCampaignName(campRes.campaignName);
          setSelected(campRes.templates || []);
          setInitial(campRes.templates || []);
          setKbFiles(campRes.kbFiles || []);
        }
      } catch (err) {
        console.error('Failed to load data', err);
        setAlert?.({ variant: 'error', heading: 'Error', text: 'Failed to load campaign.' });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [name]);
  

  const addTemplate = () => {
    setNewTemplates(t => [
      ...t,
      {
        templateName: '',
        questions: [{ questionName: '', description: '',expected: '', type: 'Compliance', weight: '', priority: 'medium'  }]
      }
    ]);
  };

  const updateTemplate = (index, key, value) => {
    setNewTemplates(t => t.map((tpl, i) => i === index ? { ...tpl, [key]: value } : tpl));
  };

  const addQuestion = (tplIndex) => {
    setNewTemplates(t => t.map((tpl, i) =>
      i === tplIndex ? { ...tpl, questions: [...tpl.questions, { questionName: '', description: '',expected: "", type: 'Compliance', weight: '', priority: 'medium' }] } : tpl
    ));
  };

  const updateQuestion = (tplIndex, qIndex, key, value) => {
    setNewTemplates(t => t.map((tpl, i) =>
      i === tplIndex ? { ...tpl, questions: tpl.questions.map((q, qi) => qi === qIndex ? { ...q, [key]: value } : q) } : tpl
    ));
  };

  const removeQuestion = (tplIndex, qIndex) => {
    setNewTemplates(t => t.map((tpl, i) =>
      i === tplIndex ? { ...tpl, questions: tpl.questions.filter((_, qi) => qi !== qIndex) } : tpl
    ));
  };

  const removeTemplate = (index) => {
    setNewTemplates(t => t.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
        for (let i =0; i<= allCamps.length -1 ; i++) {
          if (allCamps[i].campaignName === campaignName && name != allCamps[i].campaignName) {
            setInputError([true, `Campaign name '${allCamps[i].campaignName}' already exists.`])
            setSaveConfirm(false);
            return;
          }
        }
      // Same guard for inline scorecards: creating over an existing name would
      // silently overwrite that scorecard.
      for (const tpl of newTemplates) {
        if (templates.some((t) => t.templateName === tpl.templateName.trim())) {
          setInputError([true, `Scorecard '${tpl.templateName.trim()}' already exists — link it above instead of recreating it.`]);
          setSaveConfirm(false);
          return;
        }
      }
      if (!name) {
        const createdNames = [];
        for (const tpl of newTemplates) {
          if (!tpl.templateName.trim()) throw new Error('Scorecard name required');
          await createTemplateApi({ templateName: tpl.templateName, campaign: campaignName, questions: withSortOrder(tpl.questions) });
          createdNames.push(tpl.templateName);
        }
        await createCampaignApi({ campaignName, templates: [...selected, ...createdNames] });
        // Upload knowledge base files after campaign is created
        if (upload.length != 0 ){
          for (let i=0; i <= upload.length - 1; i++) {
              let filename = upload[i].name
              let s3Up = await presign(filename, `knowledgebase`, campaignName)
              await axios.put(s3Up.url, upload[i])
              // Register the file in KB management system linked to this campaign
              await addKbFileToCampaign(campaignName, filename, s3Up.key || filename);
          }
        }
      } else {
        const createdNames = [];
        for (const tpl of newTemplates) {
          if (!tpl.templateName.trim()) throw new Error('Scorecard name required');
          await createTemplateApi({ templateName: tpl.templateName, campaign: campaignName, questions: withSortOrder(tpl.questions) });
          createdNames.push(tpl.templateName);
        }
        if (upload.length != 0 ){
          for (let i=0; i <= upload.length - 1; i++) {
              let filename = upload[i].name
              let s3Up = await presign(filename, `knowledgebase`, campaignName)
              await axios.put(s3Up.url, upload[i])
              // Register the file in KB management system linked to this campaign
              await addKbFileToCampaign(campaignName, filename, s3Up.key || filename);
          }
        }
        const toAdd = [...selected, ...createdNames].filter(t => !initial.includes(t));
        const toRemove = initial.filter(t => ![...selected, ...createdNames].includes(t));
        for (const t of toAdd) {
          await updateCampaignTemplatesApi(campaignName, { templateName: t, action: 'add' });
        }
        for (const t of toRemove) {
          await updateCampaignTemplatesApi(campaignName, { templateName: t, action: 'remove' });
        }
      }
      setUpload([])
      setAlert?.({ variant: 'success', heading: 'Campaign Saved', text: 'Campaign saved successfully.' });
      setSaved(true);
    } catch (err) {
      console.error('Save failed', err);
      setAlert?.({ variant: 'error', heading: 'Error', text: 'Failed to save campaign.' });
    } finally {
      setSaving(false);
      // Close the confirm dialog on every outcome so a failure surfaces on
      // the page instead of behind the dialog.
      setSaveConfirm(false);
    }
  };

  // Delete KB file handler
  const handleDeleteKbFile = async () => {
    if (!deleteKbInfo.fileId || !name) return;
    try {
      await deleteKbFileFromCampaign(name, deleteKbInfo.fileId);
      setKbFiles(files => files.filter(f => f !== deleteKbInfo.fileId));
      setAlert?.({ variant: 'success', heading: 'File Deleted', text: 'Knowledge base file deleted successfully.' });
    } catch (err) {
      console.error('Failed to delete KB file', err);
      setAlert?.({ variant: 'error', heading: 'Error', text: 'Failed to delete knowledge base file.' });
    } finally {
      setDeleteKbInfo({ open: false, fileId: null });
    }
  };

  if (loading) return <LoadingState variant="form" label="Loading campaign" />;

  return (
    <>
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4 min-w-0">
          {name && (
            <span className="w-12 h-12 rounded-xl bg-gradient-to-br from-cloudza-500 to-cloudza-700 text-white text-lg font-bold flex items-center justify-center uppercase flex-shrink-0 shadow-sm">
              {(campaignName || name).trim().charAt(0)}
            </span>
          )}
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight truncate">{name ? 'Edit Campaign' : 'New Campaign'}</h1>
            <p className="text-gray-500 text-sm mt-1">
              {name ? 'Update knowledge base files and linked scorecards' : 'Name the campaign and link its scorecards'}
            </p>
          </div>
        </div>
      </div>
      <div className="card space-y-4">
        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-1.5">Campaign Name</label>
          <input value={campaignName} onChange={e=>setCampaignName(e.target.value)} className="input-field" disabled={!!name} />
          {name && (
            <p className="text-xs text-gray-400 mt-1.5">Campaign names can't be changed after creation.</p>
          )}
        </div>
        {/* Knowledge Base Files Section */}
        <div className="border-t border-gray-100 pt-5 mt-5">
          <div className="flex items-center gap-2.5 mb-1.5">
            <h2 className="text-sm font-bold text-gray-900">Knowledge Base Files</h2>
            {name && kbFiles.length > 0 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-cloudza-50 text-cloudza-700 text-xs font-semibold">
                {kbFiles.length}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-400 mb-3">Attach reference documents the assessor should score against.</p>
          {/* Display attached KB files */}
          {name && kbFiles.length > 0 && (
            <ul className="space-y-2 mb-4">
              {kbFiles.map((fileId) => (
                <li key={fileId} className="flex items-center gap-3 bg-gray-50 py-2 pl-3 pr-2 rounded-xl border border-gray-200">
                  <span className="w-8 h-8 rounded-lg bg-cloudza-50 text-cloudza-600 flex items-center justify-center flex-shrink-0">
                    <FaFileAlt className="text-xs" />
                  </span>
                  <span className="text-sm text-gray-700 truncate flex-1" title={fileId}>{fileId}</span>
                  <button
                    type="button"
                    onClick={() => setDeleteKbInfo({ open: true, fileId })}
                    className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors flex-shrink-0"
                    aria-label={`Delete ${fileId}`}
                    title="Delete file"
                  >
                    <FaTrash className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {name && kbFiles.length === 0 && (
            <p className="text-xs text-gray-400 mb-3">No knowledge base files attached yet.</p>
          )}
          {/* Upload new files */}
          <FileUpload
            onChange={handleFileAdd}
            value={upload}
            multiple
            buttonText="Upload examples"
          />
        </div>
        <div className="border-t border-gray-100 pt-5 mt-5">
          <div className="flex items-center gap-2.5 mb-1.5">
            <h2 className="text-sm font-bold text-gray-900">Scorecards</h2>
            {selected.length > 0 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-coral-50 text-coral-700 text-xs font-semibold">
                {selected.length} of {templates.length} linked
              </span>
            )}
          </div>
          <p className="text-xs text-gray-400 mb-3">Calls in this campaign are assessed against every linked scorecard.</p>
          {/* A dropdown rather than a card grid: the grid grew one box per
              scorecard and crowded the form (demo feedback, 2026-08-24). */}
          <MultiSelect
            options={templates.map(t => ({ value: t.templateName, label: t.templateName }))}
            value={selected}
            onChange={setSelected}
            placeholder="Select scorecards to link..."
          />
          {newTemplates.map((tpl, idx) => (
            <div key={idx} className="border border-cloudza-100 rounded-xl p-4 mt-4 space-y-4 bg-cloudza-50/40">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 pb-2.5">
                <span className="text-sm font-semibold text-gray-800">
                  New Scorecard{tpl.templateName ? `: ${tpl.templateName}` : ''}
                </span>
                <button
                  onClick={()=>removeTemplate(idx)}
                  className="btn-outline !py-1 !px-2.5 !text-xs !text-red-600 !border-red-200 hover:!bg-red-50"
                >
                  <FaTrash /> Remove Scorecard
                </button>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1.5">Scorecard Name</label>
                <input value={tpl.templateName} onChange={e=>updateTemplate(idx,'templateName',e.target.value)} className="input-field" placeholder="e.g. retention_qa" />
              </div>
              <div className="space-y-4">
                {tpl.questions.map((q, qIdx) => (
                  <div key={qIdx} className="border border-gray-200 rounded-xl p-3.5 space-y-3 bg-white">
                    <div className="flex items-center justify-between gap-2">
                      <span className="inline-flex items-center gap-2">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-cloudza-50 text-cloudza-700 text-xs font-bold">
                          {qIdx + 1}
                        </span>
                        <span className="text-xs font-semibold text-gray-600">
                          {q.questionName || `Question ${qIdx + 1}`}
                        </span>
                      </span>
                      <button
                        onClick={()=>removeQuestion(idx,qIdx)}
                        className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                        aria-label={`Remove question ${qIdx + 1}`}
                      >
                        <FaTrash className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Question Name</label>
                      <input
                        value={q.questionName}
                        onChange={e=>updateQuestion(idx,qIdx,'questionName',e.target.value)}
                        className="input-field"
                        placeholder="e.g. greeting_check"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Assessment Question</label>
                      <textarea
                        rows="2"
                        value={q.description}
                        onChange={e=>updateQuestion(idx,qIdx,'description',e.target.value)}
                        className="input-field w-full"
                        placeholder="Did the agent greet the customer correctly?"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Expected Criteria</label>
                      <textarea
                        rows="2"
                        value={q.expected}
                        onChange={e=>updateQuestion(idx, qIdx,'expected', e.target.value)}
                        className="input-field w-full"
                        placeholder="Agent should greet within 10 seconds..."
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Type</label>
                        <SelectField
                          value={q.type}
                          onChange={e=>updateQuestion(idx,qIdx,'type',e.target.value)}
                          className="select-field w-full"
                        >
                          {ASSESSMENT_TYPE_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </SelectField>
                      </div>
                      {isWeightedType(q.type) && (
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Weight</label>
                          <input
                            type="number"
                            value={q.weight}
                            onChange={e=>updateQuestion(idx,qIdx,'weight',e.target.value)}
                            className="input-field w-full"
                            placeholder="e.g. 10"
                          />
                        </div>
                      )}
                      {q.type === 'Compliance' && (
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Priority</label>
                          <SelectField
                            value={q.priority || 'medium'}
                            onChange={e=>updateQuestion(idx,qIdx,'priority',e.target.value)}
                            className="select-field w-full"
                          >
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                          </SelectField>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                <button onClick={()=>addQuestion(idx)} className="btn-outline !py-1.5 !px-3.5 !text-xs"><FaPlus /> Add Question</button>
              </div>
            </div>
          ))}
          <button onClick={addTemplate} className="btn-outline mt-4"><FaPlus />{' '}Create New Scorecard</button>
        </div>
        {inputError[0] && (
          <div className="custom-alert" ref={errorRef}>
            <span>{inputError[1]}</span>
            <button className="dismiss-btn" onClick={() => setInputError([false])}>
              <FaTimes />
            </button>
          </div>
        )}  
        <div className="flex flex-wrap justify-end gap-2 border-t border-gray-100 pt-4 mt-5">
          <button onClick={() => navigate('/campaigns')} className="btn-outline"><FaTimes /> Cancel</button>
          <button onClick={() => setSaveConfirm(true)} disabled={saving} className="btn-primary">
            {saving ? 'Saving...' : <><FaSave /> Save</>}
          </button>
        </div>
      </div>
    </div>
    <ConfirmationModal
      open={saveConfirm}
      title="Save Confirmation"
      message={`Are you sure you want to save the changes to ${campaignName || 'this campaign'}?`}
      confirmText="Confirm Save"
      onClose={() => setSaveConfirm(false)}
      onConfirm={handleSave}
    />
    <DeleteConfirmationModal
      open={deleteKbInfo.open}
      itemName={deleteKbInfo.fileId || ''}
      onClose={() => setDeleteKbInfo({ open: false, fileId: null })}
      onConfirm={handleDeleteKbFile}
    />
    <ConfirmationModal
      open={blocker.state === 'blocked'}
      title="Unsaved Changes"
      message="You have unsaved changes. Are you sure you want to leave?"
      confirmText="Leave"
      onClose={() => blocker.reset()}
      onConfirm={() => blocker.proceed()}
    />
    </>
  );
};

export default CampaignEditor;